import * as React from 'react';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { Employee } from '../../../hooks/useEmployeeData';
import TimeTrackingService from '../services/TimeTrackingService';
import ShiftService from '../../shifts/services/ShiftService';
import WorkAreaService from '../../workareas/services/WorkAreaService';
import { HttpError } from '../../../core/services/HttpService';
import globalErrorHandler from '../../../core/services/GlobalErrorHandler';
import timeTrackingErrorHandler from '../services/TimeTrackingErrorHandler';
import DateRangeFilter, { DateRange } from './DateRangeFilter';
import ChangeRequestService from '../services/ChangeRequestService';
import { ExportTTModal } from '../export/ExportTTModal';
import LocalStorageService from '../../../core/services/LocalStorageService';
import { useUserRole } from '../../../hooks/useUserRole';
import { toast as toastNotify } from 'react-toastify';
import dialogUtil from '../../../shared/utils/dialog';
import TTTable from './TTTable';
import TTAddModal from './TTAddModal';
import TTEditModal from './TTEditModal';
import TTChangeRequestModal from './TTChangeRequestModal';
import TTImportWizard from './TTImportWizard';
import { TTHeaderControls } from './TTHeaderControls';
import { TTSelectionBar } from './TTSelectionBar';
import { useCRModal } from './useCRModal';
import {
  WorkArea, TimeTracking, TimeTrackingEditFormData, SortKey,
  crRefersToThisTT, calcNetHoursNumber,
  normalizeUTC, toUTCIfNaiveLocal,
  formatDay, formatTime, calcBreak, extractTimeFromISO,
  useWorkedHoursMap, useSortedTimeTrackings, useToggleSort,
} from './useTimeTrackings';
import css from './TimeTrackings.module.css';

interface TimeTrackingsProps {
  employees?: Employee[];
  activeEmployeeId?: number | null;
  onRangeChange?: (start: Date, end: Date, displayedTTs: TimeTracking[]) => void;
  workAreas?: WorkArea[];
  selectedWAs?: number[];
  onSelectedWAsChange?: (ids: number[]) => void;
  onAddClick?: () => void;
  renderHeaderControls?: boolean;
  exportOpen?: boolean;
  onExportOpenChange?: (open: boolean) => void;
  importOpen?: boolean;
  onImportOpenChange?: (open: boolean) => void;
  addModalOpen?: boolean;
  onAddModalOpenChange?: (open: boolean) => void;
  initialMonth?: Date;
  initialRange?: { start: string; end: string };
}

const EMPTY_EMPLOYEES: Employee[] = [];

export default function TimeTrackings({
  employees = EMPTY_EMPLOYEES, activeEmployeeId = null, onRangeChange,
  workAreas: externalWorkAreas, selectedWAs: externalSelectedWAs,
  onSelectedWAsChange, onAddClick, renderHeaderControls = true,
  exportOpen: externalExportOpen, onExportOpenChange,
  importOpen: externalImportOpen, onImportOpenChange,
  addModalOpen: externalAddModalOpen, onAddModalOpenChange,
  initialMonth, initialRange,
}: TimeTrackingsProps): React.JSX.Element {
  const { role, workAreaIds, isManager } = useUserRole();

  const [timeTrackings, setTimeTrackings] = useState<TimeTracking[]>([]);
  const [internalWorkAreas, setInternalWorkAreas] = useState<WorkArea[]>([]);
  const [internalSelectedWAs, setInternalSelectedWAs] = useState<number[]>([]);
  const workAreas = externalWorkAreas ?? internalWorkAreas;
  const selectedWAs = externalSelectedWAs ?? internalSelectedWAs;
  const setSelectedWAs = (ids: number[]) => {
    if (onSelectedWAsChange) { onSelectedWAsChange(ids); } else { setInternalSelectedWAs(ids); }
  };
  const setWorkAreas = externalWorkAreas ? () => {} : setInternalWorkAreas;

  const [dateRange, setDateRange] = useState<DateRange | null>(null);
  const [loading, setLoading] = useState(true);
  const [plannedHoursMap, setPlannedHoursMap] = useState<Record<number, number>>({});
  const [approvedCrMap, setApprovedCrMap] = useState<Record<number, { start: string; end: string }>>({});
  const [fileInputKey, setFileInputKey] = useState(0);
  const [uiLedger, setUiLedger] = useState<Record<number, { originalStart: string; originalEnd: string }>>({});
  useEffect(() => { try { setUiLedger(LocalStorageService.getTtUiLedger() as any); } catch {} }, []);
  const persistLedger = (next: Record<number, { originalStart: string; originalEnd: string }>) => {
    setUiLedger(next); LocalStorageService.setTtUiLedger(next);
  };

  const [pendingCrMap, setPendingCrMap] = useState<Record<number, { start: string; end: string }>>({});
  const [restoredOriginalMap, setRestoredOriginalMap] = useState<Record<number, { start: string; end: string }>>({});
  const [selectedTTIds, setSelectedTTIds] = useState<Set<number>>(new Set());
  const [crPresenceTick, setCrPresenceTick] = useState(0);
  const [displayLimit, setDisplayLimit] = useState(50);

  const [internalExportOpen, setInternalExportOpen] = useState(false);
  const exportOpen = externalExportOpen ?? internalExportOpen;
  const setExportOpen = (open: boolean) => {
    if (onExportOpenChange) { onExportOpenChange(open); } else { setInternalExportOpen(open); }
  };

  const [bulkMenuOpen, setBulkMenuOpen] = useState(false);
  const [bulkWAOpen, setBulkWAOpen] = useState(false);
  const [bulkWorkAreaId, setBulkWorkAreaId] = useState<number | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  const currentRangeStr = useMemo(() => {
    if (!dateRange) { const t = new Date().toISOString().split('T')[0]; return { from: t, to: t }; }
    return { from: dateRange.start.toISOString().split('T')[0], to: dateRange.end.toISOString().split('T')[0] };
  }, [dateRange]);

  const employeeNameById = useCallback((id: number | string): string | undefined => {
    const idNum = typeof id === 'string' ? Number(id) : id;
    const emp = employees.find(e => e.id === idNum);
    return emp ? `${emp.first_name} ${emp.last_name}` : undefined;
  }, [employees]);

  const activeEmployeeName = useMemo(() => {
    if (!activeEmployeeId) return null;
    const emp = employees.find(e => e.id === activeEmployeeId) || null;
    return emp ? `${emp.first_name} ${emp.last_name}` : null;
  }, [activeEmployeeId, employees]);

  const [internalImportOpen, setInternalImportOpen] = useState(false);
  const importOpen = externalImportOpen ?? internalImportOpen;
  const setImportOpen = (open: boolean) => {
    if (onImportOpenChange) { onImportOpenChange(open); } else { setInternalImportOpen(open); }
  };
  const openImportModal = () => { setImportOpen(true); setFileInputKey(k => k + 1); };

  const [toast, setToast] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  useEffect(() => { if (!toast) return; const t = setTimeout(() => setToast(null), 4000); return () => clearTimeout(t); }, [toast]);

  const workedHoursMap = useWorkedHoursMap(timeTrackings);
  const [sortBy, setSortBy] = useState<SortKey>('day');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const sortedTimeTrackings = useSortedTimeTrackings(timeTrackings, sortBy, sortDir, employees, plannedHoursMap, workedHoursMap);
  const toggleSort = useToggleSort(sortBy, setSortBy, setSortDir);

  const [showAddInternal, setShowAddInternal] = useState(false);
  const showAdd = externalAddModalOpen !== undefined ? externalAddModalOpen : showAddInternal;
  const setShowAdd = (open: boolean) => {
    if (onAddModalOpenChange) { onAddModalOpenChange(open); } else { setShowAddInternal(open); }
  };

  const [showEdit, setShowEdit] = useState(false);
  const [editData, setEditData] = useState<TimeTrackingEditFormData>({
    id: null, employee: '', work_area: '', date: '', start_time: '', end_time: '', break_start: '', break_end: '',
  });

  useEffect(() => {
    (async () => {
      try { const was = await WorkAreaService.fetchWorkAreas(); setWorkAreas(was); }
      catch (error) { console.error('WA load =>', error); if (error instanceof HttpError) globalErrorHandler.handle(error); }
    })();
  }, []);

  useEffect(() => {
    if (isManager && workAreaIds.length > 0 && workAreas.length > 0 && selectedWAs.length === 0) setSelectedWAs(workAreaIds);
  }, [isManager, workAreaIds, workAreas, selectedWAs.length]);

  const stableOnRangeChange = useCallback((s: Date, e: Date, tts: TimeTracking[]) => {
    if (onRangeChange) onRangeChange(s, e, tts);
  }, [onRangeChange]);

  const crPresenceCacheRef = React.useRef<Record<number, boolean>>({});
  const markCrPresence = (ttId: number, has: boolean) => { crPresenceCacheRef.current[ttId] = has; };

  const loadTimeTrackings = useCallback(async (range: DateRange, waIds: number[], employeeId?: number | null) => {
    if (!range) return;
    setLoading(true);
    try {
      const params: any = { start_date: range.start.toISOString().split('T')[0], end_date: range.end.toISOString().split('T')[0] };
      if (waIds.length > 0) params.work_area = waIds.join(',');
      if (employeeId) params.employee = employeeId;
      const fetchedTTs = await TimeTrackingService.fetchTimeTrackingsWithChangeRequests(params);
      setTimeTrackings(fetchedTTs);
      const pMap: Record<number, { start: string; end: string }> = {};
      const apprMap: Record<number, { start: string; end: string }> = {};
      fetchedTTs.forEach(tt => {
        const cr = (tt as any).active_change_request;
        crPresenceCacheRef.current[tt.id] = cr?.status === 'PENDING';
        if (cr?.status === 'PENDING') {
          const s = toUTCIfNaiveLocal((cr as any).new_start_time || (cr as any).requested_start_time || (cr as any).proposed_start_time || tt.start_time);
          const e = toUTCIfNaiveLocal((cr as any).new_end_time || (cr as any).requested_end_time || (cr as any).proposed_end_time || tt.end_time);
          if (s && e) pMap[tt.id] = { start: s, end: e };
        } else if (cr?.status === 'APPROVED') {
          const s = toUTCIfNaiveLocal((cr as any).new_start_time);
          const e = toUTCIfNaiveLocal((cr as any).new_end_time);
          if (s && e) apprMap[tt.id] = { start: s, end: e };
        }
      });
      setPendingCrMap(pMap); setApprovedCrMap(apprMap); setCrPresenceTick(prev => prev + 1);
      const weeklyOpts = { start: params.start_date, days: Math.max(1, Math.round((range.end.getTime() - range.start.getTime()) / 86400000)), work_area: waIds.length > 0 ? waIds.join(',') : undefined } as any;
      const weekly = await ShiftService.fetchWeeklyShifts(weeklyOpts);
      const map: Record<number, number> = {};
      const now = new Date(); const plannedStart = now > range.start ? now : range.start;
      weekly.forEach(dayObj => {
        if (!dayObj.shifts) return;
        dayObj.shifts.forEach(sh => {
          const st = new Date(sh.start_time); const et = new Date(sh.end_time);
          if (!(st >= plannedStart && st < range.end)) return;
          const diffH = Math.max(0, (et.getTime() - st.getTime()) / 3600000);
          if (sh.assigned_employees) sh.assigned_employees.forEach(aEmp => { map[aEmp.id] = (map[aEmp.id] || 0) + diffH; });
        });
      });
      setPlannedHoursMap(map);
      if (onRangeChange) onRangeChange(range.start, range.end, fetchedTTs);
    } catch (error) {
      console.error('TT load =>', error);
      if (error instanceof HttpError) { if (!timeTrackingErrorHandler.handle(error)) globalErrorHandler.handle(error); }
    } finally { setLoading(false); }
  }, [stableOnRangeChange]);

  useEffect(() => {
    setDisplayLimit(50);
    if (dateRange && selectedWAs.length > 0) loadTimeTrackings(dateRange, selectedWAs, activeEmployeeId);
    else if (selectedWAs.length === 0) setTimeTrackings([]);
  }, [dateRange, selectedWAs, activeEmployeeId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    (async () => {
      try {
        const missingIds: number[] = [];
        timeTrackings.forEach(tt => {
          const cr = (tt as any).active_change_request;
          if (Boolean(crRefersToThisTT(tt, cr) && cr?.status === 'PENDING') && !pendingCrMap[tt.id]) missingIds.push(tt.id);
        });
        if (missingIds.length === 0) return;
        const fetched = await Promise.all(missingIds.map(async id => {
          try {
            const cr = await ChangeRequestService.fetchByTimeTrackingId(id, 'PENDING');
            if (!cr) return null;
            const s = toUTCIfNaiveLocal((cr as any)?.new_start_time || (cr as any)?.requested_start_time || (cr as any)?.proposed_start_time || (cr as any)?.original_start_time || (cr as any)?.original?.start_time);
            const e = toUTCIfNaiveLocal((cr as any)?.new_end_time || (cr as any)?.requested_end_time || (cr as any)?.proposed_end_time || (cr as any)?.original_end_time || (cr as any)?.original?.end_time);
            if (s && e) return [id, { start: s, end: e }] as [number, { start: string; end: string }];
            return null;
          } catch { return null; }
        }));
        const upd: Record<number, { start: string; end: string }> = {};
        fetched.forEach(item => { if (item) { const [id, val] = item; upd[id] = val; } });
        if (Object.keys(upd).length > 0) setPendingCrMap(prev => ({ ...prev, ...upd }));
      } catch {}
    })();
  }, [timeTrackings, crPresenceTick, pendingCrMap]);

  useEffect(() => {
    (async () => {
      if (!timeTrackings || timeTrackings.length === 0) return;
      const candidates = timeTrackings.filter(tt => {
        const a = tt as any;
        if (a.active_change_request) return false;
        const hints = a.has_pending_change_request === true || a.has_change_request === true;
        const unknown = a.has_pending_change_request === undefined && a.has_change_request === undefined;
        return (hints || unknown) && pendingCrMap[tt.id] === undefined && approvedCrMap[tt.id] === undefined;
      });
      if (candidates.length === 0) return;
      const updates: Record<number, { start: string; end: string }> = {};
      await Promise.allSettled(candidates.map(async tt => {
        try {
          const cr = await ChangeRequestService.fetchByTimeTrackingId(tt.id);
          if (!cr || !crRefersToThisTT(tt, cr)) return;
          if ((cr as any).status === 'PENDING') {
            const s = toUTCIfNaiveLocal((cr as any).new_start_time || (cr as any).requested_start_time);
            const e = toUTCIfNaiveLocal((cr as any).new_end_time || (cr as any).requested_end_time);
            if (s && e) updates[tt.id] = { start: s, end: e };
            markCrPresence(tt.id, true);
          } else if ((cr as any).status === 'APPROVED') {
            const s = toUTCIfNaiveLocal((cr as any).new_start_time);
            const e = toUTCIfNaiveLocal((cr as any).new_end_time);
            if (s && e) setApprovedCrMap(prev => ({ ...prev, [tt.id]: { start: s!, end: e! } }));
            markCrPresence(tt.id, false);
          }
        } catch {}
      }));
      if (Object.keys(updates).length > 0) { setPendingCrMap(prev => ({ ...prev, ...updates })); setCrPresenceTick(t => t + 1); }
    })();
  }, [timeTrackings, pendingCrMap, approvedCrMap]);

  const handleDateRangeChange = useCallback((range: DateRange) => { setDateRange(range); }, []);

  const handleDeleteTT = async (ttId: any) => {
    if (!await dialogUtil.confirm('Delete Time Tracking', `Delete TimeTracking #${ttId}?`, { variant: 'error', confirmLabel: 'Delete' })) return;
    try { await TimeTrackingService.deleteTimeTracking(ttId); setTimeTrackings(prev => prev.filter(tt => tt.id !== ttId)); }
    catch (error) {
      console.error("Error deleting TT =>", error);
      if (error instanceof HttpError) { if (!timeTrackingErrorHandler.handle(error)) globalErrorHandler.handle(error); }
      else toastNotify.error('Error deleting time tracking');
    }
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedTTIds); if (ids.length === 0) return;
    if (!await dialogUtil.confirm('Delete Time Trackings', `Möchtest du wirklich ${ids.length} Zeiterfassungen löschen?`, { variant: 'error', confirmLabel: 'Löschen' })) return;
    try {
      const results = await Promise.allSettled(ids.map(id => TimeTrackingService.deleteTimeTracking(id)));
      const deletedIds = ids.filter((_, i) => results[i].status === 'fulfilled');
      if (deletedIds.length > 0) setTimeTrackings(prev => prev.filter(tt => !deletedIds.includes(tt.id)));
      setSelectedTTIds(prev => { const next = new Set(prev); deletedIds.forEach(id => next.delete(id)); return next; });
      if (results.filter(r => r.status === 'rejected').length > 0) toastNotify.error(`Einige Einträge konnten nicht gelöscht werden.`);
    } catch { toastNotify.error('Fehler beim Löschen der Zeiterfassungen'); }
  };

  const handleBulkDeleteChangeRequests = async () => {
    const ids = Array.from(selectedTTIds); if (ids.length === 0) return;
    if (!window.confirm(`Change Requests für ${ids.length} Einträge löschen?`)) return;
    setBulkBusy(true);
    try {
      const allCrIds = new Set<number>();
      for (const id of ids) {
        try {
          const p = await ChangeRequestService.fetchByTimeTrackingId(id, 'PENDING');
          const a = await ChangeRequestService.fetchByTimeTrackingId(id, 'APPROVED');
          if (p && (p as any).id) allCrIds.add((p as any).id);
          if (a && (a as any).id) allCrIds.add((a as any).id);
          if (!p && !a) { const c = await ChangeRequestService.fetchByTimeTrackingId(id); if (c && (c as any).id) allCrIds.add((c as any).id); }
        } catch {}
      }
      if (allCrIds.size === 0) { toastNotify.info('Keine Change Requests gefunden.'); return; }
      await Promise.allSettled(Array.from(allCrIds).map(crId => ChangeRequestService.deleteChangeRequest(crId)));
      const selIds = Array.from(selectedTTIds);
      setApprovedCrMap(prev => { const n: any = { ...prev }; selIds.forEach(id => delete n[id]); return n; });
      setPendingCrMap(prev => { const n: any = { ...prev }; selIds.forEach(id => delete n[id]); return n; });
      setRestoredOriginalMap(prev => { const n: any = { ...prev }; selIds.forEach(id => delete n[id]); return n; });
      selIds.forEach(id => markCrPresence(id, false));
      if (dateRange) await loadTimeTrackings(dateRange, selectedWAs, activeEmployeeId);
    } finally { setBulkBusy(false); }
  };

  const applyBulkChangeWorkArea = async () => {
    if (!bulkWorkAreaId) return;
    const ids = Array.from(selectedTTIds); if (ids.length === 0) return;
    setBulkBusy(true);
    try {
      const results = await Promise.allSettled(ids.map(id => TimeTrackingService.updateTimeTracking(id, { work_area: bulkWorkAreaId } as any)));
      if (results.filter(r => r.status === 'rejected').length > 0) toastNotify.error('Einige Updates fehlgeschlagen.');
      if (dateRange) await loadTimeTrackings(dateRange, selectedWAs, activeEmployeeId);
      setBulkWAOpen(false); setBulkWorkAreaId(null);
    } catch { toastNotify.error('Bulk-Änderung Work Area fehlgeschlagen'); } finally { setBulkBusy(false); }
  };

  const crModal = useCRModal({
    timeTrackings, uiLedger, persistLedger,
    setApprovedCrMap, setPendingCrMap, setRestoredOriginalMap,
    markCrPresence, dateRange, selectedWAs, activeEmployeeId, loadTimeTrackings,
  });

  const handleEditTT = (tt: any) => {
    const sdt = new Date(tt.start_time);
    let bsStr = '', beStr = '';
    if (tt.breakperiods && tt.breakperiods.length > 0) {
      bsStr = extractTimeFromISO(tt.breakperiods[0].start_time);
      beStr = extractTimeFromISO(tt.breakperiods[0].end_time);
    }
    setEditData({
      id: tt.id, employee: tt.employee || '', work_area: tt.work_area || '',
      date: sdt.toISOString().split('T')[0], start_time: extractTimeFromISO(tt.start_time),
      end_time: extractTimeFromISO(tt.end_time), break_start: bsStr, break_end: beStr,
    });
    setShowEdit(true);
  };

  const crPillStyle: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 24, height: 24, padding: 0, background: '#ffffff', border: '1px solid #e5e7eb',
    borderRadius: '50%', cursor: 'pointer', fontSize: '0.72rem',
    fontWeight: 500, color: '#64748b', lineHeight: '18px', transition: 'all 0.2s ease',
  };

  return (
    <div data-testid="tt-root" className={css.root}>
      {renderHeaderControls && (
        <TTHeaderControls
          workAreas={workAreas} selectedWAs={selectedWAs} setSelectedWAs={setSelectedWAs}
          isManager={isManager} workAreaIds={workAreaIds} selectedTTIds={selectedTTIds}
          setShowAdd={setShowAdd} setBulkMenuOpen={setBulkMenuOpen} bulkMenuOpen={bulkMenuOpen}
          onBulkChangeWA={() => setBulkWAOpen(true)} onBulkDeleteCRs={handleBulkDeleteChangeRequests}
          onBulkDelete={handleBulkDelete} setExportOpen={setExportOpen} openImportModal={openImportModal}
          dateRange={dateRange} setDateRange={setDateRange} activeEmployeeId={activeEmployeeId}
          employees={employees} handleDateRangeChange={handleDateRangeChange}
          initialMonth={initialMonth} initialRange={initialRange}
        />
      )}
      {!renderHeaderControls && (
        <div data-testid="tt-range-filter" className={css.rangeFilter}>
          <DateRangeFilter onRangeChange={handleDateRangeChange} defaultMode="thisMonth" initialMonth={initialMonth} initialRange={initialRange} />
        </div>
      )}

      <div className={css.tableScroll}>
        <TTTable
          loading={loading} selectedWAs={selectedWAs} timeTrackings={timeTrackings}
          sortedTimeTrackings={sortedTimeTrackings} displayLimit={displayLimit}
          setDisplayLimit={setDisplayLimit} selectedTTIds={selectedTTIds}
          setSelectedTTIds={setSelectedTTIds} sortBy={sortBy} sortDir={sortDir}
          toggleSort={toggleSort} employees={employees} workAreas={workAreas}
          plannedHoursMap={plannedHoursMap} workedHoursMap={workedHoursMap}
          approvedCrMap={approvedCrMap} pendingCrMap={pendingCrMap}
          restoredOriginalMap={restoredOriginalMap} uiLedger={uiLedger}
          crPillStyle={crPillStyle} formatDay={formatDay} formatTime={formatTime}
          calcBreak={calcBreak} calcNetHoursNumber={calcNetHoursNumber}
          normalizeUTC={normalizeUTC} toUTCIfNaiveLocal={toUTCIfNaiveLocal}
          handleEditTT={handleEditTT} handleDeleteTT={handleDeleteTT}
          openCRViewModal={crModal.openCRViewModal}
          openCRApprovedModal={crModal.openCRApprovedModal}
          handleReverseClick={crModal.handleReverseClick}
        />
      </div>

      <TTAddModal open={showAdd} onClose={() => setShowAdd(false)}
        onSave={() => { if (dateRange) loadTimeTrackings(dateRange, selectedWAs, activeEmployeeId); }}
        employees={employees} workAreas={workAreas} activeEmployeeId={activeEmployeeId} />
      <TTEditModal open={showEdit} onClose={() => setShowEdit(false)}
        onSave={() => { if (dateRange) loadTimeTrackings(dateRange, selectedWAs, activeEmployeeId); }}
        editData={editData} setEditData={setEditData} employees={employees} workAreas={workAreas} />
      <TTChangeRequestModal
        visible={crModal.crViewModalVisible} onClose={crModal.closeCRViewModal}
        crExisting={crModal.crExisting} setCrExisting={crModal.setCrExisting}
        crViewLoading={crModal.crViewLoading} crViewError={crModal.crViewError}
        crEditMode={crModal.crEditMode} setCrEditMode={crModal.setCrEditMode}
        crEditError={crModal.crEditError} setCrEditError={crModal.setCrEditError}
        crConflicts={crModal.crConflicts} setCrConflicts={crModal.setCrConflicts}
        crModalMode={crModal.crModalMode} crSourceTT={crModal.crSourceTT}
        crEditStartDate={crModal.crEditStartDate} setCrEditStartDate={crModal.setCrEditStartDate}
        crEditEndDate={crModal.crEditEndDate} setCrEditEndDate={crModal.setCrEditEndDate}
        crEditBreakMinutes={crModal.crEditBreakMinutes} setCrEditBreakMinutes={crModal.setCrEditBreakMinutes}
        crEditStartTime={crModal.crEditStartTime} setCrEditStartTime={crModal.setCrEditStartTime}
        crEditEndTime={crModal.crEditEndTime} setCrEditEndTime={crModal.setCrEditEndTime}
        crEditNotes={crModal.crEditNotes} setCrEditNotes={crModal.setCrEditNotes}
        crWorkAreaId={crModal.crWorkAreaId} setCrWorkAreaId={crModal.setCrWorkAreaId}
        employees={employees} workAreas={workAreas}
        setApprovedCrMap={setApprovedCrMap} setPendingCrMap={setPendingCrMap}
        setRestoredOriginalMap={setRestoredOriginalMap} markCrPresence={markCrPresence}
        setTimeTrackings={setTimeTrackings} dateRange={dateRange} selectedWAs={selectedWAs}
        activeEmployeeId={activeEmployeeId} loadTimeTrackings={loadTimeTrackings}
        handleReverseClick={crModal.handleReverseClick}
        toUTCIfNaiveLocal={toUTCIfNaiveLocal} normalizeUTC={normalizeUTC} extractTimeFromISO={extractTimeFromISO}
      />

      <TTSelectionBar
        bulkWAOpen={bulkWAOpen} setBulkWAOpen={setBulkWAOpen} bulkBusy={bulkBusy}
        bulkWorkAreaId={bulkWorkAreaId} setBulkWorkAreaId={setBulkWorkAreaId}
        selectedTTIds={selectedTTIds} workAreas={workAreas}
        applyBulkChangeWorkArea={applyBulkChangeWorkArea} toast={toast} />
      <ExportTTModal open={exportOpen} onClose={() => setExportOpen(false)}
        currentRange={currentRangeStr} currentWorkAreaIds={selectedWAs}
        activeEmployeeId={activeEmployeeId} activeEmployeeName={activeEmployeeName}
        employeeNameById={employeeNameById} />
      <TTImportWizard open={importOpen} onClose={() => setImportOpen(false)}
        onImportComplete={() => { if (dateRange) loadTimeTrackings(dateRange, selectedWAs, activeEmployeeId); }}
        employees={employees} workAreas={workAreas}
        activeEmployeeId={activeEmployeeId} activeEmployeeName={activeEmployeeName}
        currentRangeStr={currentRangeStr} dateRange={dateRange} selectedWAs={selectedWAs}
        loadTimeTrackings={loadTimeTrackings} setTimeTrackings={setTimeTrackings}
        setApprovedCrMap={setApprovedCrMap} setPendingCrMap={setPendingCrMap}
        setRestoredOriginalMap={setRestoredOriginalMap} markCrPresence={markCrPresence}
        uiLedger={uiLedger} persistLedger={persistLedger} setToast={setToast} setFileInputKey={setFileInputKey} />
    </div>
  );
}
